import { useState, useMemo } from 'react';
import { Building2, Package, ChevronDown, ChevronRight, MapPin, Check, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { LongTermTenderVenue, VenueSelection, Machine, MachineTier } from '../../types/map';
import Map, { MapMarker } from '../Map';
import { CountdownTimer } from '../CountdownTimer';

interface VenueSelectionStepProps {
  venues: LongTermTenderVenue[];
  venueSelections: VenueSelection[];
  onVenueSelectionsChange: (selections: VenueSelection[]) => void;
}

export function VenueSelectionStep({
  venues,
  venueSelections,
  onVenueSelectionsChange,
}: VenueSelectionStepProps) {
  const [expandedVenues, setExpandedVenues] = useState<Set<string>>(new Set());
  const [expandedTiers, setExpandedTiers] = useState<Set<string>>(new Set()); // Track expanded tiers
  const [selectedMachines, setSelectedMachines] = useState<Record<string, Set<string>>>({});

  const toggleVenue = (venueName: string) => {
    const newExpanded = new Set(expandedVenues);
    if (newExpanded.has(venueName)) {
      newExpanded.delete(venueName);
    } else {
      newExpanded.add(venueName);
    }
    setExpandedVenues(newExpanded);
  };

  const handleSelectAll = (venueName: string) => {
    const venue = venues.find((v) => v.venueName === venueName);
    if (!venue) return;

    const allMachineIds = new Set(venue.machines.map((m) => m.id));
    setSelectedMachines((prev) => ({
      ...prev,
      [venueName]: allMachineIds,
    }));

    updateVenueSelection(venueName, venue.availableMachines, Array.from(allMachineIds));
  };

  const handleDeselectAll = (venueName: string) => {
    setSelectedMachines((prev) => {
      const updated = { ...prev };
      delete updated[venueName];
      return updated;
    });

    const existingIndex = venueSelections.findIndex((s) => s.venueName === venueName);
    if (existingIndex >= 0) {
      const updated = [...venueSelections];
      updated.splice(existingIndex, 1);
      onVenueSelectionsChange(updated);
    }
  };

  const handleMachineToggle = (venueName: string, machineId: string) => {
    const venue = venues.find((v) => v.venueName === venueName);
    if (!venue) return;

    setSelectedMachines((prev) => {
      const venueMachines = new Set(prev[venueName] || []);
      if (venueMachines.has(machineId)) {
        venueMachines.delete(machineId);
      } else {
        venueMachines.add(machineId);
      }

      const updated = { ...prev };
      if (venueMachines.size > 0) {
        updated[venueName] = venueMachines;
      } else {
        delete updated[venueName];
      }

      updateVenueSelection(venueName, venueMachines.size, Array.from(venueMachines));
      return updated;
    });
  };

  const updateVenueSelection = (venueName: string, count: number, machineIds: string[]) => {
    const venue = venues.find((v) => v.venueName === venueName);
    if (!venue) return;

    const minMachines = venue.minimumMachines || 1;
    const validCount = Math.max(minMachines, Math.min(count, venue.availableMachines));

    const existingIndex = venueSelections.findIndex((s) => s.venueName === venueName);

    if (validCount === 0) {
      // Remove selection
      if (existingIndex >= 0) {
        const updated = [...venueSelections];
        updated.splice(existingIndex, 1);
        onVenueSelectionsChange(updated);
      }
      return;
    }

    // Use the provided machineIds, or take first N machines
    const finalMachineIds = machineIds.length > 0 
      ? machineIds.slice(0, validCount)
      : venue.machines.slice(0, validCount).map((m) => m.id);

    if (existingIndex >= 0) {
      const updated = [...venueSelections];
      updated[existingIndex] = {
        venueName,
        machineCount: validCount,
        machineIds: finalMachineIds,
      };
      onVenueSelectionsChange(updated);
    } else {
      onVenueSelectionsChange([
        ...venueSelections,
        {
          venueName,
          machineCount: validCount,
          machineIds: finalMachineIds,
        },
      ]);
    }
  };

  const handlePresetQuantity = (venueName: string, quantity: number) => {
    const venue = venues.find((v) => v.venueName === venueName);
    if (!venue) return;

    const machineIds = venue.machines.slice(0, quantity).map((m) => m.id);
    setSelectedMachines((prev) => ({
      ...prev,
      [venueName]: new Set(machineIds),
    }));

    updateVenueSelection(venueName, quantity, machineIds);
  };

  // Calculate map center and markers for a tier
  const getTierMapData = (tier: MachineTier) => {
    if (tier.machines.length === 0) {
      return { center: [51.5074, -0.1278] as [number, number], zoom: 10, markers: [] };
    }

    const lats = tier.machines.map((m) => m.lat);
    const lngs = tier.machines.map((m) => m.lng);

    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

    const markers: MapMarker[] = tier.machines.map((m) => ({
      lat: m.lat,
      lng: m.lng,
      title: m.name,
      color: '#3B82F6',
    }));

    // Calculate zoom level based on spread
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const maxSpread = Math.max(latSpread, lngSpread);
    
    let zoom = 15;
    if (maxSpread > 0.1) zoom = 12;
    else if (maxSpread > 0.05) zoom = 13;
    else if (maxSpread > 0.01) zoom = 14;

    return {
      center: [centerLat, centerLng] as [number, number],
      zoom,
      markers,
    };
  };

  // Calculate map center and markers for a venue (includes machines from tiers)
  const getVenueMapData = (venue: LongTermTenderVenue) => {
    // Collect all machines: direct machines + machines from tiers
    const allMachines = [
      ...venue.machines,
      ...(venue.tiers || []).flatMap((tier) => tier.machines),
    ];
    
    if (allMachines.length === 0) {
      return { center: [51.5074, -0.1278] as [number, number], zoom: 10, markers: [] };
    }

    const lats = allMachines.map((m) => m.lat);
    const lngs = allMachines.map((m) => m.lng);

    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

    const markers: MapMarker[] = allMachines.map((m) => ({
      lat: m.lat,
      lng: m.lng,
      title: m.name,
      color: '#3B82F6',
    }));

    // Calculate zoom level based on spread
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const maxSpread = Math.max(latSpread, lngSpread);
    
    let zoom = 15;
    if (maxSpread > 0.1) zoom = 12;
    else if (maxSpread > 0.05) zoom = 13;
    else if (maxSpread > 0.01) zoom = 14;

    return {
      center: [centerLat, centerLng] as [number, number],
      zoom,
      markers,
    };
  };

  const toggleTier = (tierId: string) => {
    const newExpanded = new Set(expandedTiers);
    if (newExpanded.has(tierId)) {
      newExpanded.delete(tierId);
    } else {
      newExpanded.add(tierId);
    }
    setExpandedTiers(newExpanded);
  };

  // Generate preset quantity options for large venues
  const getPresetQuantities = (venue: LongTermTenderVenue): number[] => {
    const total = venue.availableMachines;
    if (total <= 10) return []; // No presets for small venues

    const presets: number[] = [];
    
    // Always include "Select All"
    presets.push(total);

    // For very large venues (50+), add common options
    if (total >= 50) {
      // Add 70% and other common percentages
      const seventyPercent = Math.round(total * 0.7);
      if (seventyPercent !== total && seventyPercent >= venue.minimumMachines || 1) {
        presets.push(seventyPercent);
      }
      
      // Add 50% if it's different
      const fiftyPercent = Math.round(total * 0.5);
      if (fiftyPercent !== seventyPercent && fiftyPercent !== total && fiftyPercent >= (venue.minimumMachines || 1)) {
        presets.push(fiftyPercent);
      }
    }

    // For medium venues (20-49), add half option
    if (total >= 20 && total < 50) {
      const half = Math.round(total / 2);
      if (half !== total && half >= (venue.minimumMachines || 1)) {
        presets.push(half);
      }
    }

    return presets.sort((a, b) => b - a); // Sort descending
  };

  // Initialize selected machines from venue selections
  useMemo(() => {
    const initial: Record<string, Set<string>> = {};
    venueSelections.forEach((selection) => {
      initial[selection.venueName] = new Set(selection.machineIds);
    });
    setSelectedMachines(initial);
  }, [venueSelections]); // Update when venueSelections prop changes

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Select Venues & Machines</h2>
      </div>

      {venues.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p>No long-term tender venues available at this time.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {venues.map((venue) => {
            const isExpanded = expandedVenues.has(venue.venueName);
            const selection = venueSelections.find((s) => s.venueName === venue.venueName);
            const venueSelectedMachines = selectedMachines[venue.venueName] || new Set<string>();
            const selectedCount = venueSelectedMachines.size || 0;
            const minMachines = venue.minimumMachines || 1;
            const presetQuantities = getPresetQuantities(venue);
            const mapData = getVenueMapData(venue);

            return (
              <Card 
                key={venue.venueName}
              >
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleVenue(venue.venueName)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      )}
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Building2 className="w-5 h-5" />
                          {venue.venueName}
                        </CardTitle>
                      </div>
                    </div>
                    <div className="flex items-center justify-center min-h-[40px]">
                      {venue.tenderLaunchDate && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <CountdownTimer deadline={venue.tenderLaunchDate} label="" compact={true} />
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {/* Show tiers if they exist */}
                      {venue.tiers && venue.tiers.length > 0 && (
                        <div className="space-y-4 mb-4 pb-4">
                          <Label className="text-sm font-medium">Select Tiers:</Label>
                          <div className="space-y-4">
                            {venue.tiers.map((tier) => {
                              const tierMachineIds = new Set(tier.machines.map((m) => m.id));
                              const tierSelectedCount = Array.from(venueSelectedMachines).filter(
                                (id) => tierMachineIds.has(id)
                              ).length;
                              const tierFullySelected = tier.machines.length > 0 && 
                                tier.machines.every((m) => venueSelectedMachines.has(m.id));
                              const isTierExpanded = expandedTiers.has(tier.id);
                              const tierMapData = getTierMapData(tier);
                              
                              return (
                                <div
                                  key={tier.id}
                                  className="rounded-lg overflow-hidden"
                                >
                                  {/* Tier Header */}
                                  <div
                                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                                      tierFullySelected ? 'bg-blue-50' : ''
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (tierFullySelected) {
                                        // Deselect all tier machines
                                        setSelectedMachines((prev) => {
                                          const updated = new Set(prev[venue.venueName] || []);
                                          tier.machines.forEach((m) => updated.delete(m.id));
                                          const newState = { ...prev };
                                          if (updated.size > 0) {
                                            newState[venue.venueName] = updated;
                                          } else {
                                            delete newState[venue.venueName];
                                          }
                                          updateVenueSelection(venue.venueName, newState[venue.venueName]?.size || 0, Array.from(newState[venue.venueName] || []));
                                          return newState;
                                        });
                                      } else {
                                        // Select all tier machines
                                        const allTierIds = tier.machines.map((m) => m.id);
                                        setSelectedMachines((prev) => {
                                          const updated = new Set(prev[venue.venueName] || []);
                                          allTierIds.forEach((id) => updated.add(id));
                                          const newState = { ...prev };
                                          newState[venue.venueName] = updated;
                                          updateVenueSelection(venue.venueName, updated.size, Array.from(updated));
                                          return newState;
                                        });
                                      }
                                    }}
                                  >
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                      tierFullySelected 
                                        ? 'bg-blue-600 border-blue-600' 
                                        : 'border-slate-300'
                                    }`}>
                                      {tierFullySelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{tier.name}</span>
                                        <span className="text-xs font-normal text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                          Tier
                                        </span>
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        {tierFullySelected 
                                          ? `${tier.availableMachines} Machine${tier.availableMachines !== 1 ? 's' : ''} Selected`
                                          : `${tier.availableMachines} Machine${tier.availableMachines !== 1 ? 's' : ''} Total`
                                        }
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="default"
                                      size="sm"
                                      className="h-8 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleTier(tier.id);
                                      }}
                                    >
                                      {isTierExpanded ? 'Hide' : 'View'}
                                    </Button>
                                  </div>

                                  {/* Collapsible Machine List and Map View */}
                                  {isTierExpanded && (
                                    <div className="p-3 bg-slate-50">
                                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {/* Machine List */}
                                        <div className="space-y-2">
                                          <Label className="text-sm font-medium">Machines in Tier:</Label>
                                          <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                                            {tier.machines.map((machine) => {
                                              const isSelected = venueSelectedMachines.has(machine.id);
                                              return (
                                                <div
                                                  key={machine.id}
                                                  className={`flex items-center gap-3 p-3 border-b last:border-0 ${
                                                    isSelected ? 'bg-blue-50 border-blue-200' : ''
                                                  }`}
                                                >
                                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                                    isSelected 
                                                      ? 'bg-blue-600 border-blue-600' 
                                                      : 'border-slate-300'
                                                  }`}>
                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                  </div>
                                                  <div className="flex-1">
                                                    <div className="font-medium text-sm">{machine.name}</div>
                                                    {machine.location && (
                                                      <div className="text-xs text-slate-500">{machine.location}</div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          <div className="text-xs text-slate-500">
                                            {tierFullySelected 
                                              ? `${tier.availableMachines} Machine${tier.availableMachines !== 1 ? 's' : ''} Selected`
                                              : `${tierSelectedCount} of ${tier.availableMachines} Machine${tier.availableMachines !== 1 ? 's' : ''} Selected`
                                            }
                                          </div>
                                        </div>

                                        {/* Map View */}
                                        <div className="space-y-2">
                                          <Label className="text-sm font-medium flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            Machine Locations
                                          </Label>
                                          <div className="border rounded-lg overflow-hidden relative z-0" style={{ height: '300px' }}>
                                            <Map
                                              center={tierMapData.center}
                                              zoom={tierMapData.zoom}
                                              markers={tierMapData.markers}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Preset quantity buttons for large venues (only if no tiers or if there are non-tier machines) */}
                      {presetQuantities.length > 0 && (!venue.tiers || venue.tiers.length === 0 || venue.machines.length > 0) && (
                        <div>
                          <Label className="text-sm font-medium mb-2 block">Quick Select:</Label>
                          <div className="flex flex-wrap gap-2">
                            {presetQuantities.map((qty) => (
                              <Button
                                key={qty}
                                type="button"
                                variant={selectedCount === qty ? 'default' : 'outline'}
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePresetQuantity(venue.venueName, qty);
                                }}
                              >
                                {qty === venue.availableMachines ? 'Select All' : `${qty} Machines`}
                              </Button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Machine list and map in a grid - only show if there are machines not in tiers */}
                      {venue.machines.length > 0 && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {/* Machine List */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-sm font-medium">Select Individual Machines:</Label>
                              {venue.machines.length > 0 && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const nonTierMachineIds = new Set(venue.machines.map((m) => m.id));
                                    const allNonTierSelected = venue.machines.every((m) => venueSelectedMachines.has(m.id));
                                    
                                    if (allNonTierSelected) {
                                      // Deselect all non-tier machines
                                      setSelectedMachines((prev) => {
                                        const updated = new Set(prev[venue.venueName] || []);
                                        venue.machines.forEach((m) => updated.delete(m.id));
                                        const newState = { ...prev };
                                        if (updated.size > 0) {
                                          newState[venue.venueName] = updated;
                                        } else {
                                          delete newState[venue.venueName];
                                        }
                                        updateVenueSelection(venue.venueName, newState[venue.venueName]?.size || 0, Array.from(newState[venue.venueName] || []));
                                        return newState;
                                      });
                                    } else {
                                      // Select all non-tier machines
                                      setSelectedMachines((prev) => {
                                        const updated = new Set(prev[venue.venueName] || []);
                                        venue.machines.forEach((m) => updated.add(m.id));
                                        const newState = { ...prev };
                                        newState[venue.venueName] = updated;
                                        updateVenueSelection(venue.venueName, updated.size, Array.from(updated));
                                        return newState;
                                      });
                                    }
                                  }}
                                >
                                  {venue.machines.every((m) => venueSelectedMachines.has(m.id)) ? 'Deselect All' : 'Select All'}
                                </Button>
                              )}
                            </div>
                            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                              {venue.machines.map((machine) => {
                                const isSelected = venueSelectedMachines.has(machine.id);
                                return (
                                  <div
                                    key={machine.id}
                                    className={`flex items-center gap-3 p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${
                                      isSelected ? 'bg-blue-50 border-blue-200' : ''
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMachineToggle(venue.venueName, machine.id);
                                    }}
                                  >
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      isSelected 
                                        ? 'bg-blue-600 border-blue-600' 
                                        : 'border-slate-300'
                                    }`}>
                                      {isSelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-sm">{machine.name}</div>
                                      {machine.location && (
                                        <div className="text-xs text-slate-500">{machine.location}</div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="text-xs text-slate-500">
                              {venueSelectedMachines.size} of {venue.availableMachines} machine{venue.availableMachines !== 1 ? 's' : ''} selected
                              {minMachines > 1 && (
                                <span className="text-red-600 ml-2">(Minimum: {minMachines})</span>
                              )}
                            </div>
                          </div>

                        {/* Map View */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Machine Locations
                          </Label>
                          <div className="border rounded-lg overflow-hidden relative z-0" style={{ height: '300px' }}>
                            <Map
                              center={mapData.center}
                              zoom={mapData.zoom}
                              markers={mapData.markers}
                            />
                          </div>
                        </div>
                      </div>
                      )}

                      {/* Venue Info */}
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        {venue.planogramConfig && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Package className="w-4 h-4" />
                            <span>
                              Planogram: {venue.totalCapacity} SKU positions per machine
                            </span>
                          </div>
                        )}
                        {venue.tenderTimeframe && (
                          <div className="text-sm text-slate-600">
                            <strong>Listing Timeframe:</strong> {venue.tenderTimeframe}
                          </div>
                        )}
                        {venue.tenderLaunchDate && (
                          <div className="text-sm text-slate-600">
                            <strong>Launch Date:</strong> {new Date(venue.tenderLaunchDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}
